// ** React Imports
import React, { useState, useEffect, useRef } from 'react'
import { NavLink, useNavigate, useLocation } from 'react-router-dom'

// ** Store & Actions
import { useSelector, useDispatch } from 'react-redux'
import { handleMenuCollapsed, handleContentWidth, handleMenuHidden } from '@store/actions/layout'
// ** Third Party Components
import classnames from 'classnames'
import { Badge, Breadcrumb, Drawer } from 'dsm-react-lib'

// Constants
import { staticAssetsPath } from '@configs/constants'

// ** Configs
import themeConfig from '@configs/themeConfig'

// ** Custom Components
import Customizer from '@components/customizer'
import FooterComponent from './components/footer'
import SidebarComponent from './components/menu/new-vertical-menu'

// ** Custom Hooks
import { useRTL } from '@hooks/useRTL'
import { useSkin } from '@hooks/useSkin'
import { useNavbarType } from '@hooks/useNavbarType'
import { useFooterType } from '@hooks/useFooterType'
import { useNavbarColor } from '@hooks/useNavbarColor'

// ** Styles
import '@styles/base/core/menu/menu-types/vertical-menu.scss'
import '@styles/base/core/menu/menu-types/vertical-overlay-menu.scss'
import Chatbot from '../../views/_pages/Chatbot/Chatbot'
import { checkIsDrawerMode } from '../../views/_pages/Chatbot/utils/helpers'
import { setIsChatOpen } from '../../_redux/actions/dataMarketPlaceActions'
import { useMediaQuery } from '@react-hook/media-query'
import {
  ExitFocusIcon,
  FocusModeIcon,
  NotificationIcon
} from '../../views/_pages/Chatbot/icons/Icons'
import { UncontrolledTooltip } from 'reactstrap'

// ** Notification Components
import { Notifications } from '../containers/Notifications'
import { useNotificationService } from '../../_services/NotificationService'
import { modifyTopbarStyle } from '../../utility/Utils'

import { setBreadCrumbMapper } from '../../_redux/actions/breadCrumbMapperAction'
import {
  IconArrowsMaximize,
  IconArrowsMinimize,
  IconBell,
  IconHelp,
  IconHome,
  IconLayoutSidebarLeftCollapse,
  IconLayoutSidebarLeftExpand,
  IconChevronRight
} from '@tabler/icons-react'
import SupportForm from '../../views/Support/SupportForm'
import { useSasToken } from '../../_hooks/useSasToken'
import { useThemeManager } from '../../_hooks/useThemeManager'
import WorkspaceTopbar from '../../views/_pages/Work-space/WorkspaceTopbar'
import { useFeatureGate } from '../../_hooks/useFeatureGate'

// ** Constants
const DEFAULT_PANEL_WIDTH_PERCENTAGE = 0.35 // 35vw
const MAX_PANEL_WIDTH_PERCENTAGE = 0.5 // 50vw

const defaultBreadcrumbs = {
  label: 'My space',
  icon: (
    <IconHome className="topbar-breadcrumb-home-icon !w-[16px] !h-[16px] theme-text-subheading" />
  )
}

const TopBarIcon = ({ children, isTopbarDark, ...props }) => (
  <div
    className={`relative flex h-[48px] w-[48px] cursor-pointer items-center justify-center border-l outline-none transition-colors ${
      isTopbarDark
        ? 'border-[var(--sidebar-divider)] theme-hover-surface'
        : 'border-[var(--sidebar-divider)] theme-hover-surface'
    }`}
    {...props}>
    {children}
  </div>
)

// ** Menu toggler component
const Toggler = ({ menuCollapsed, setMenuCollapsed, isTopbarDark, isWorkspaceRoute }) => (
  <span className={`ml-[-12px] flex h-full cursor-pointer items-center`} id="toggle-sidebar-icon">
    {!menuCollapsed ? (
      <IconLayoutSidebarLeftCollapse
        size={22}
        data-tour="toggle-icon"
        className={`outline-none ${
          isTopbarDark ? 'text-[var(--icon-color-200)]' : 'theme-text-subheading'
        }`}
        onClick={() => setMenuCollapsed(true)}
      />
    ) : (
      <IconLayoutSidebarLeftExpand
        size={22}
        data-tour="toggle-icon"
        className={`outline-none ${
          isTopbarDark ? 'text-[var(--icon-color-200)]' : 'theme-text-subheading'
        }`}
        onClick={() => {
          // Prevent expanding menu on workspace routes
          if (!isWorkspaceRoute) {
            setMenuCollapsed(false)
          }
        }}
      />
    )}
    <UncontrolledTooltip target="toggle-sidebar-icon" placement="right">
      {menuCollapsed ? 'Open sidebar' : 'Close sidebar'}
    </UncontrolledTooltip>
  </span>
)

const VerticalLayout = (props) => {
  // ** Props
  const { children, navbar, footer, menu, routerProps, currentActiveItem } = props

  // ** Hooks
  const [skin, setSkin] = useSkin()
  const [isRtl, setIsRtl] = useRTL()
  const [navbarType, setNavbarType] = useNavbarType()
  const [footerType, setFooterType] = useFooterType()
  const [navbarColor, setNavbarColor] = useNavbarColor()

  // ** States
  const [isMounted, setIsMounted] = useState(false)
  const [menuVisibility, setMenuVisibility] = useState(false)
  const [windowWidth, setWindowWidth] = useState(window.innerWidth)
  const [isFullScreenMode, setIsFullScreenMode] = useState(false)
  const [openNotificationSidebar, setOpenNotificationSidebar] = useState(false)
  const [appIcons, setAppIcons] = useState()
  const [supportModalOpen, setSupportModalOpen] = useState(false)
  const [currentPanelWidth, setCurrentPanelWidth] = useState(
    () => window.innerWidth * DEFAULT_PANEL_WIDTH_PERCENTAGE
  )
  const { appendToken } = useSasToken()
  const { isDarkTheme } = useThemeManager()
  const navigate = useNavigate()
  const smallScreen = useMediaQuery('(max-width: 1800px)')

  // ** Store Vars
  const dispatch = useDispatch()
  const layoutStore = useSelector((state) => state.layoutReducer)
  const allNotifications = useSelector(
    (state) => state.notificationContextReducer.notificationContext
  )
  const { isChatOpen } = useSelector((state) => state.dataMarketPlaceReducer)
  const drawerWidthRef = useRef(window.innerWidth * DEFAULT_PANEL_WIDTH_PERCENTAGE)
  const drawerRef = useRef(null)
  const resizerRef = useRef(null)
  const { value: centrali3_0, isLoading: isCentrali3_0Loading } = useFeatureGate(
    'centrali3_0',
    false
  )

  // ** Update Window Width
  const handleWindowWidth = () => {
    setWindowWidth(window.innerWidth)
  }

  // ** Notification Service
  const { getIcons, refreshNotifications, refreshNewCount, markAllNotificationsAsSeen } =
    useNotificationService()

  const notificationsCount = allNotifications?.newCount || 0
  // ** Vars
  const location = useLocation()
  const contentWidth = layoutStore.contentWidth
  const menuCollapsed = layoutStore.menuCollapsed
  const isHidden = layoutStore.menuHidden
  const isSidebarVisible = !isHidden && windowWidth >= 1200

  // ** Initialize focus mode from Redux state to avoid desync on page refresh
  const [isFocusMode, setIsFocusMode] = useState(isHidden)

  // Check if current route should show chatbot
  const isDrawerMode = checkIsDrawerMode(location.pathname)

  const handleChatToggle = () => {
    dispatch(setIsChatOpen(!isChatOpen))
  }

  // ** Toggles Menu Collapsed
  const setMenuCollapsed = (val) => {
    // Prevent menu expansion on workspace routes
    if (location.pathname.includes('/workspace') && val === false) {
      return
    }
    dispatch(handleMenuCollapsed(val))
  }

  // ** Handles Content Width
  const setContentWidth = (val) => dispatch(handleContentWidth(val))

  // ** Handles Content Width
  const setIsHidden = (val) => dispatch(handleMenuHidden(val))

  //Breadcrumb Mapper from Redux Store
  const breadcrumbMapper = useSelector((state) => state?.breadCrumbReducer?.breadCrumbMapper)
  // Set Breadcrumb Mapper on Component Mount
  useEffect(() => {
    const homeBreadcrumb = {
      ...defaultBreadcrumbs
    }
    dispatch(setBreadCrumbMapper([homeBreadcrumb]))
  }, [dispatch])

  //** This function will detect the Route Change and will hide the menu on menu item click
  useEffect(() => {
    if (menuVisibility && windowWidth < 1200) {
      setMenuVisibility(false)
    }
    // Collapse menu when navigating to workspace
    if (location.pathname.includes('/workspace')) {
      setMenuCollapsed(true)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentionally only re-run on route change
  }, [location])

  // ** Keep menu collapsed on workspace routes when window resizes
  useEffect(() => {
    if (location.pathname.includes('/workspace')) {
      setMenuCollapsed(true)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentionally only re-run on width change
  }, [windowWidth])

  //** Sets Window Size & Layout Props
  useEffect(() => {
    if (window !== undefined) {
      window.addEventListener('resize', handleWindowWidth)
    }
    return () => {
      if (window !== undefined) {
        window.removeEventListener('resize', handleWindowWidth)
      }
    }
  }, [])

  //** ComponentDidMount
  useEffect(() => {
    setIsMounted(true)
    return () => setIsMounted(false)
  }, [])

  //** Handle menu collapse on small screens
  useEffect(() => {
    if (smallScreen && isChatOpen && isDrawerMode) {
      dispatch(handleMenuCollapsed(true))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- isDrawerMode derives from location, don't re-run on every route change
  }, [smallScreen, isChatOpen])

  // ** Toggle focus mode - prevents desync by storing new value before updating both local and Redux state
  const handleFocusMode = () => {
    const newFocusMode = !isFocusMode
    setIsFocusMode(newFocusMode)
    setIsHidden(newFocusMode)
    // Close menu when entering focus mode
    if (newFocusMode) {
      setMenuVisibility(false)
    }
  }

  // ** Keyboard shortcut handler for focus mode (Alt + U)
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.altKey && event.key.toLowerCase() === 'u') {
        handleFocusMode()
      }
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('keydown', handleKeyDown)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- handleFocusMode recreated each render, only re-bind on isFocusMode change
  }, [isFocusMode])

  // ** Sync isFocusMode with Redux isHidden state
  useEffect(() => {
    if (isHidden !== isFocusMode) {
      setIsFocusMode(isHidden)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only re-sync when Redux state changes
  }, [isHidden])

  const handleFullScreenMode = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen()
      setIsFullScreenMode(true)
    } else {
      document.exitFullscreen()
      setIsFullScreenMode(false)
    }
  }

  // ** Fullscreen mode handler (Alt + Esc)
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.altKey && event.key.toLowerCase() === 'esc') {
        handleFullScreenMode()
      }
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [])

  const handleMouseDown = (e) => {
    const startX = e.clientX
    const startWidth = drawerWidthRef.current

    // Prevent text selection while dragging
    document.body.style.userSelect = 'none'

    const handleMouseMove = (event) => {
      const newWidth = startWidth - (event.clientX - startX)
      const minWidth = window.innerWidth * DEFAULT_PANEL_WIDTH_PERCENTAGE
      const maxWidth = window.innerWidth * MAX_PANEL_WIDTH_PERCENTAGE

      drawerWidthRef.current = Math.max(minWidth, Math.min(newWidth, maxWidth))

      // Update state to trigger re-render of styled components
      setCurrentPanelWidth(drawerWidthRef.current)

      if (drawerRef.current) {
        drawerRef.current.style.width = `${drawerWidthRef.current}px`
      }

      // Highlight resizer while moving
      if (resizerRef.current) {
        resizerRef.current.style.background = 'var(--color-primary)'
      }
    }

    const handleMouseUp = () => {
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)

      // Allow text selection again
      document.body.style.userSelect = 'auto'

      // Remove highlight
      if (resizerRef.current) {
        resizerRef.current.style.background = 'transparent'
      }
    }

    document.addEventListener('mousemove', handleMouseMove)
    document.addEventListener('mouseup', handleMouseUp)
  }

  // ** Notification Sidebar Effect
  useEffect(() => {
    const fetchAppIcons = async () => {
      modifyTopbarStyle(openNotificationSidebar, menuCollapsed || isFocusMode)
      if (openNotificationSidebar) {
        const res = await refreshNotifications(30)
        if (allNotifications?.newCount > 0) markAllNotificationsAsSeen()
        const fetchAppIcons = async () => {
          const appIds = res.map(({ applicationId }) => applicationId)
          const response = await getIcons([...new Set(appIds)])
          setAppIcons(response)
        }
        if (res && res.length > 0) {
          fetchAppIcons()
        } else {
          setAppIcons([])
        }
      } else {
        refreshNewCount()
      }
    }
    fetchAppIcons()
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only re-run when the sidebar is toggled
  }, [openNotificationSidebar])

  // ** Toggle Notification Sidebar
  const toggleNotificationSidebar = () => {
    setOpenNotificationSidebar(!openNotificationSidebar)
  }

  // ** Vars
  const footerClasses = {
    static: 'footer-static',
    sticky: 'footer-fixed',
    hidden: 'footer-hidden'
  }

  const navbarWrapperClasses = {
    floating: 'navbar-floating',
    sticky: 'navbar-sticky',
    static: 'navbar-static',
    hidden: 'navbar-hidden'
  }

  const showIcon = (menuCollapsed && windowWidth >= 1200) || isFocusMode
  const isTopbarDark = isDarkTheme || skin === 'dark'

  const getLogo = () => {
    if (!isDarkTheme) {
      return showIcon || isFocusMode
        ? appendToken(`${staticAssetsPath}/images/logo/Centrali-Icon.svg`)
        : appendToken(`${staticAssetsPath}/images/logo/Centrali-Portal.svg`)
    } else {
      return showIcon || isFocusMode
        ? appendToken(`${staticAssetsPath}/images/logo/Centrali-Icon-dark.svg`)
        : appendToken(`${staticAssetsPath}/images/logo/Centrali-Portal-dark.svg`)
    }
  }

  if (!isMounted) {
    return null
  }

  // Calculate topbar dimensions based on layout state
  const sidebarWidth = menuCollapsed ? 80 : 260
  const isChatDrawerOpen = isChatOpen && isDrawerMode

  const getTopbarWidth = () => {
    if (windowWidth <= 1200 || isFocusMode || isHidden) return '100%'
    if (isChatDrawerOpen) return `calc(100% - ${currentPanelWidth}px - ${sidebarWidth}px)`
    if (menuCollapsed) return `calc(100% - ${sidebarWidth}px)`
    return 'calc(100vw - (100vw - 100%) - 0.1rem - 260px)'
  }

  return (
    <div
      className={`wrapper vertical-layout ${
        navbarWrapperClasses[navbarType] || 'navbar-floating'
      } ${footerClasses[footerType] || 'footer-static'} ${
        windowWidth >= 1200 && !isFocusMode ? 'vertical-menu-modern' : 'vertical-overlay-menu'
      } ${
        menuCollapsed && windowWidth >= 1200 && !isFocusMode
          ? 'menu-collapsed'
          : !menuCollapsed && windowWidth > 1200 && !isFocusMode
          ? 'menu-expanded'
          : ''
      } ${
        !menuVisibility && (windowWidth < 1200 || isFocusMode)
          ? 'menu-hide'
          : menuVisibility && (windowWidth < 1200 || isFocusMode)
          ? 'menu-open'
          : ''
      }`}
      {...(isHidden && !menuVisibility ? { 'data-col': '1-column' } : {})}>
      {(!isHidden || (isFocusMode && menuVisibility)) && (
        <SidebarComponent
          skin={skin}
          menu={menu}
          menuCollapsed={isFocusMode && menuVisibility ? false : menuCollapsed}
          menuVisibility={menuVisibility}
          setMenuCollapsed={setMenuCollapsed}
          setMenuVisibility={setMenuVisibility}
          routerProps={routerProps}
          currentActiveItem={currentActiveItem}
          isFocusMode={isFocusMode}
          isSidebarVisible={isSidebarVisible || (isFocusMode && menuVisibility)}
        />
      )}

      {/* Nav Topbar */}
      <div
        className={`app-topbar-frame fixed right-0 flex h-[40px] items-center border-b border-r-0 border-l-0 border-t-0 theme-frame-divider ${
          location.pathname.includes('/workspace') && centrali3_0
            ? // Per the M&G design, the workspace tab strip uses the deep background
              // so it visibly contrasts with the app/widget tab strip (--surface-muted)
              // sitting directly below it.
              'bg-[var(--bg-color)]'
            : 'theme-surface-muted'
        } ${isSidebarVisible || (isFocusMode && menuVisibility) ? 'z-[3]' : 'z-[1]'}`}
        style={{
          width: getTopbarWidth(),
          right: isChatDrawerOpen ? `${currentPanelWidth}px` : '0px'
        }}>
        {isSidebarVisible && !location.pathname.includes('/workspace') && (
          <Toggler
            menuCollapsed={menuCollapsed}
            setMenuCollapsed={setMenuCollapsed}
            isTopbarDark={isTopbarDark}
            isWorkspaceRoute={location.pathname.includes('/workspace')}
          />
        )}
        <div className={`flex justify-between h-full w-full`}>
          {location.pathname.includes('/workspace') && centrali3_0 ? (
            <WorkspaceTopbar />
          ) : (
            <div className={`flex justify-start align-items-center`}>
              {(isFocusMode || !isSidebarVisible) && !menuVisibility && (
                <>
                  <IconLayoutSidebarLeftExpand
                    size={24}
                    className={`ml-2 cursor-pointer outline-none theme-text-subheading`}
                    id="open-sidebar"
                    onClick={() => {
                      // Prevent expanding menu on workspace routes
                      if (!location.pathname.includes('/workspace')) {
                        if (isFocusMode) {
                          setMenuVisibility(true)
                        } else {
                          setMenuCollapsed(false)
                          setMenuVisibility(true)
                        }
                      }
                    }}
                  />
                  <UncontrolledTooltip target="open-sidebar" placement="right">
                    Open sidebar
                  </UncontrolledTooltip>
                </>
              )}
              {isFocusMode && menuVisibility && (
                <>
                  <IconLayoutSidebarLeftCollapse
                    size={24}
                    className={`ml-2 cursor-pointer outline-none theme-text-subheading`}
                    id="close-sidebar-overlay"
                    onClick={() => setMenuVisibility(false)}
                  />
                  <UncontrolledTooltip target="close-sidebar-overlay" placement="right">
                    Close sidebar
                  </UncontrolledTooltip>
                </>
              )}
              {isFocusMode && !menuVisibility && (
                <NavLink to={'/my-space'} className={`navbar-brand mt-0`}>
                  <img
                    src={getLogo()}
                    alt="logo"
                    height={32}
                    width={40}
                    className="transition-all duration-300 ease-in-out"
                  />
                </NavLink>
              )}
              <div
                className={`layout-topbar-breadcrumb flex space-x-2 items-center justify-center cursor-pointer ${
                  !isSidebarVisible
                    ? 'ml-[9px]'
                    : !isFocusMode && !menuCollapsed
                    ? 'ml-[16px]'
                    : menuCollapsed && !isFocusMode
                    ? 'ml-[9px]'
                    : ''
                }`}>
                <Breadcrumb
                  color="neutral"
                  items={breadcrumbMapper}
                  inverseColors={true}
                  separator={<IconChevronRight />}
                />
              </div>
            </div>
          )}
          <div className="flex items-center">
            <TopBarIcon
              id="notification-bell-icon"
              onClick={toggleNotificationSidebar}
              isTopbarDark={isTopbarDark}>
              {notificationsCount > 0 && (
                <Badge
                  color="secondary"
                  className="!absolute !top-2 !right-3 !w-[16px] !h-[16px] !min-w-[16px] !min-h-[16px] !p-0 !flex !items-center !justify-center !text-[8px] !rounded-full !aspect-square"
                  size="16px"
                  label={notificationsCount > 99 ? '99+' : notificationsCount}
                />
              )}
              {notificationsCount > 0 ? (
                <NotificationIcon size={16} color="var(--icon-color-200)" />
              ) : (
                <IconBell size={20} color="var(--icon-color-200)" />
              )}
              <UncontrolledTooltip
                target="notification-bell-icon"
                placement="bottom"
                className="mt-1">
                {notificationsCount > 0
                  ? `You have ${notificationsCount} new notifications`
                  : 'No new notifications'}
              </UncontrolledTooltip>
            </TopBarIcon>
            <TopBarIcon
              id="help-icon"
              onClick={() => setSupportModalOpen(true)}
              isTopbarDark={isTopbarDark}>
              <IconHelp size={20} color={'var(--icon-color-200)'} />
              <UncontrolledTooltip target="help-icon" placement="bottom" className="mt-1">
                Need help?
              </UncontrolledTooltip>
            </TopBarIcon>
            <TopBarIcon onClick={handleFocusMode} id="focus-mode-icon" isTopbarDark={isTopbarDark}>
              {!isFocusMode ? (
                <FocusModeIcon size={16} color="var(--icon-color-200)" />
              ) : (
                <ExitFocusIcon size={16} color="var(--icon-color-200)" />
              )}
              {isFocusMode ? (
                <UncontrolledTooltip target="focus-mode-icon" placement="bottom" className="mt-1">
                  Exit focus mode{' '}
                  <code className="bg-[var(--keyboard-shortcut-keycap-bg)] text-[var(--keyboard-shortcut-keycap-text)]">
                    Alt + U
                  </code>
                </UncontrolledTooltip>
              ) : (
                <UncontrolledTooltip target="focus-mode-icon" placement="bottom" className="mt-1">
                  Enter focus mode{' '}
                  <code className="bg-[var(--keyboard-shortcut-keycap-bg)] text-[var(--keyboard-shortcut-keycap-text)]">
                    Alt + U
                  </code>
                </UncontrolledTooltip>
              )}
            </TopBarIcon>
            <TopBarIcon
              id="fullScreen-mode-icon"
              onClick={handleFullScreenMode}
              isTopbarDark={isTopbarDark}>
              {!isFullScreenMode ? (
                <>
                  <IconArrowsMaximize size={18} color="var(--icon-color-200)" />
                  <UncontrolledTooltip
                    target="fullScreen-mode-icon"
                    placement="bottom"
                    className="mt-1">
                    Full screen
                  </UncontrolledTooltip>
                </>
              ) : (
                <>
                  <IconArrowsMinimize size={18} color="var(--icon-color-200)" />
                  <UncontrolledTooltip
                    target="fullScreen-mode-icon"
                    placement="bottom"
                    className="mt-1">
                    Exit full screen
                    <code className="bg-[var(--keyboard-shortcut-keycap-bg)] text-[var(--keyboard-shortcut-keycap-text)]">
                      Alt + Esc
                    </code>
                  </UncontrolledTooltip>
                </>
              )}
            </TopBarIcon>
          </div>
        </div>
      </div>

      {/* Notification Drawer */}
      <Drawer
        className="w-[35vw] theme-drawer notification-centre-drawer"
        header={
          <div className="flex items-center justify-between w-full pr-2">
            <h4 className="subsection-header mb-0 p-1">Notification centre</h4>
          </div>
        }
        hasCloseButton={true}
        isOpen={openNotificationSidebar}
        setIsOpen={() => setOpenNotificationSidebar(false)}
        closeOnOutsideClick={true}
        overlay={true}>
        {openNotificationSidebar && (
          <Notifications toggle={toggleNotificationSidebar} appIcons={appIcons} />
        )}
      </Drawer>

      <div className="layout-container flex h-[calc(100%-48px)]">
        <div
          className="relative z-0 flex h-full grow flex-col overflow-x-hidden overflow-y-auto transition-[width] duration-[400ms] ease-in-out"
          // Dynamic width calculation with runtime variable - Tailwind cannot generate classes for JavaScript variables
          style={{
            width: isChatOpen && isDrawerMode ? `calc(100% - ${currentPanelWidth}px)` : '100%'
          }}>
          {children}

          {/* Vertical Nav Menu Overlay */}
          {themeConfig.layout.customizer === true ? (
            <Customizer
              skin={skin}
              setSkin={setSkin}
              footerType={footerType}
              setFooterType={setFooterType}
              navbarType={navbarType}
              setNavbarType={setNavbarType}
              navbarColor={navbarColor}
              setNavbarColor={setNavbarColor}
              isRtl={isRtl}
              setIsRtl={setIsRtl}
              layout={props.layout}
              setLayout={props.setLayout}
              isHidden={isHidden}
              setIsHidden={setIsHidden}
              contentWidth={contentWidth}
              setContentWidth={setContentWidth}
              menuCollapsed={menuCollapsed}
              setMenuCollapsed={setMenuCollapsed}
              transition={props.transition}
              setTransition={props.setTransition}
              themeConfig={themeConfig}
            />
          ) : null}
        </div>

        {isChatOpen && isDrawerMode && (
          <div className="right-0 top-0 z-[1000] flex h-full overflow-hidden">
            <div
              className={`z-[1000] left-0 w-[6px] h-full cursor-ew-resize hover:bg-[var(--color-primary)]`}
              onMouseDown={handleMouseDown}
              ref={resizerRef}
            />
            <div
              className={`flex-shrink-0 transition-[width] duration-[400ms] ease-in-out relative max-w-[50vw] ${
                isChatOpen ? 'min-w-[30vw]' : ''
              }`}
              // Dynamic width kept in sync with drawerWidthRef during resize - cannot use Tailwind
              style={{
                width: `${currentPanelWidth}px`
              }}
              ref={drawerRef}>
              <Chatbot />
            </div>
          </div>
        )}
      </div>
      <footer
        className={classnames(
          `footer ${
            footerClasses[footerType] || 'footer-static'
          } theme-surface-muted theme-text-subheading theme-border-default`,
          {
            'd-none': footerType === 'hidden'
          }
        )}>
        {footer ? (
          footer({ footerType, footerClasses })
        ) : (
          <FooterComponent footerType={footerType} footerClasses={footerClasses} />
        )}
      </footer>
      {supportModalOpen && (
        <Drawer
          className="theme-drawer support-drawer"
          header={
            <div className="flex items-center justify-between w-full pr-2">
              <h4 className="subsection-header mb-0 p-1">Support</h4>
            </div>
          }
          hasCloseButton={true}
          isOpen={supportModalOpen}
          setIsOpen={() => setSupportModalOpen(false)}
          closeOnOutsideClick={true}
          overlay={true}>
          {supportModalOpen && <SupportForm />}
        </Drawer>
      )}

      {/* {themeConfig.layout.scrollTop === true ? (
        <div className="scroll-to-top">
          <ScrollToTop showUnder={300} style={{ bottom: '5%' }}>
            <Button className="btn-icon" color="primary"
              icon={<ArrowUp size={14} />}>
            </Button>
          </ScrollToTop>
        </div>
      ) : null} */}
    </div>
  )
}

export { defaultBreadcrumbs }
export default VerticalLayout
